import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import {
	formatPaymentRunPostedMessage,
	hasPaymentRunPostings,
	PaymentsService
} from '../../services/payments.service';
import { PrintService } from '../../services/print.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
	VendorRemitPreviewModalComponent,
	VendorRemitPreviewModalData,
	VendorRemitPreviewTabRequest
} from '../../shared/vendor-remit-preview-modal/vendor-remit-preview-modal.component';

@Component({
	selector: 'app-payment-run-review',
	templateUrl: './payment-run-review.component.html',
	styleUrls: ['./payment-run-review.component.scss']
})
export class PaymentRunReviewComponent implements OnInit, OnDestroy {
	@ViewChild('paymentRunPrintArea', { static: false }) paymentRunPrintArea: ElementRef | null = null;
	private destroy$ = new Subject<void>();

	journal: number | string | null = null;
	run: any = null;
	loading = true;
	error: string | null = null;
	posting = false;
	saving = false;
	successMessage: string | null = null;
	/** After successful Approve & post — show summary until user leaves. */
	postedResult: any = null;
	color = 'blue';
	editMode = false;
	editableRemittance: any[] = [];
	summaryCollapsed = false;
	readonly POST_CHUNK_SIZE = 10;
	postingProgress: {
		current: number;
		total: number;
		posted: number;
		skipped: number;
		errors: any[];
		failed?: boolean;
	} | null = null;

	// Where to pick the run back up after a mid-run failure. A chunk that failed
	// client-side (timeout) may still have committed server-side, so we resume from the
	// last chunk we got a clean response for. The server's journal-scoped idempotency
	// check is the backstop if this index is ever behind what actually posted.
	private postResumeIndex = 0;

	// Add-invoices (edit mode): the vendor's other open invoices, so the user can
	// pull in transactions that weren't part of the original run selection.
	addPanelFor: string | null = null;
	loadingAddFor: string | null = null;
	availableByVendor: { [supplierId: string]: any[] } = {};
	addError: string | null = null;

	// Payment-method chips (edit mode): change a vendor's payment type before resave/post.
	paymentMethods: any[] = [];

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private globalSearchService: GlobalSearchService,
		private paymentsService: PaymentsService,
		private printService: PrintService,
		private dialog: MatDialog
	) {
		this.color = this.globalSearchService.getColor();
	}

	ngOnInit(): void {
		// Same payment-method list the check-printing screen uses; chip value is paymentname.
		this.globalSearchService.payment_methods.pipe(takeUntil(this.destroy$)).subscribe((methods: any) => {
			this.paymentMethods = methods || [];
		});
		this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(paramMap => {
			const j = paramMap.get('journal');
			if (j) {
				this.journal = j;
				this.loadRun();
			} else {
				this.journal = null;
				this.error = 'Missing run identifier.';
				this.loading = false;
				this.run = null;
			}
		});
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	loadRun(): void {
		if (this.journal == null || this.journal === '') return;
		this.loading = true;
		this.posting = false;
		this.postingProgress = null;
		this.postResumeIndex = 0;
		this.error = null;
		this.postedResult = null;
		this.successMessage = null;
		this.editMode = false;
		this.editableRemittance = [];
		const journalParam = typeof this.journal === 'string' && /^\d+$/.test(this.journal) ? parseInt(this.journal, 10) : this.journal;
		this.paymentsService.loadRunData({ journal: journalParam }).subscribe(
			(res: any) => {
				this.loading = false;
				if (res && res.success === true) {
					this.run = {
						success: true,
						journal: res.journal,
						checks: res.checks || [],
						postings: res.postings != null ? res.postings : {},
						fullpostings: res.fullpostings,
						remittance: Array.isArray(res.remittance) ? res.remittance : [],
						savedPayload: res.savedPayload && typeof res.savedPayload === 'object' ? res.savedPayload : this.buildSavedPayloadFromRemittance(res)
					};
					if ((this.run.remittance.length === 0) && this.run.postings && typeof this.run.postings === 'object' && !Array.isArray(this.run.postings)) {
						this.run.remittance = this.remittanceFromPostings(this.run.postings);
					}
					if (!this.run.savedPayload || (Object.keys(this.run.savedPayload.include || {}).length === 0 && this.run.remittance.length > 0)) {
						this.run.savedPayload = this.buildSavedPayloadFromRemittance({ remittance: this.run.remittance, postings: this.run.postings });
					}
				} else {
					this.run = null;
					this.error = (res && res.error) ? res.error : 'Run not found or already posted.';
				}
			},
			(err: any) => {
				this.loading = false;
				this.run = null;
				this.error = err?.error?.error || err?.message || 'Run not found or already posted.';
			}
		);
	}

	private remittanceFromPostings(postings: any): any[] {
		if (!postings || typeof postings !== 'object' || Array.isArray(postings)) return [];
		return Object.keys(postings).map((k: string) => {
			const p = postings[k];
			const allocs = p.Allocs || [];
			return {
				PaytReference: p.PaytReference,
				PayOrder: p.PayOrder,
				PostDate: p.PostDate,
				AccumBalance: p.AccumBalance,
				Currency: p.Currency,
				PaytType: p.PaytType,
				SupplierID: p.SupplierID,
				lines: allocs.map((a: any) => ({
					TransID: a.TransID,
					suppreference: '',
					trandate: '',
					amount: a.Amount,
					discount: 0
				}))
			};
		});
	}

	private buildSavedPayloadFromRemittance(data: { remittance?: any[]; postings?: any; savedPayload?: any; [k: string]: any }): any {
		const remittance = Array.isArray(data.remittance) ? data.remittance : [];
		const include: any = {};
		const payamt: any = {};
		const discamt: any = {};
		const date: any = {};
		for (const rem of remittance) {
			if (rem.SupplierID != null) date[rem.SupplierID] = rem.PostDate || '';
			for (const line of rem.lines || []) {
				const id = line.TransID;
				if (id != null) {
					include[id] = true;
					payamt[id] = line.amount != null ? Number(line.amount) : 0;
					discamt[id] = line.discount != null ? Number(line.discount) : 0;
				}
			}
		}
		const sp = data.savedPayload && typeof data.savedPayload === 'object' ? data.savedPayload : {};
		return {
			include: Object.keys(include).length ? include : (sp.include || {}),
			payamt: Object.keys(payamt).length ? payamt : (sp.payamt || {}),
			discamt: Object.keys(discamt).length ? discamt : (sp.discamt || {}),
			date: Object.keys(date).length ? date : (sp.date || {}),
			bankacct: sp.bankacct != null ? sp.bankacct : '',
			currency: sp.currency != null ? sp.currency : 'USD',
			duedate: sp.duedate != null ? sp.duedate : '',
			checkNum: sp.checkNum != null ? sp.checkNum : '',
			vendorCheckNumbers: sp.vendorCheckNumbers && typeof sp.vendorCheckNumbers === 'object' ? sp.vendorCheckNumbers : {},
			paymentTypes: sp.paymentTypes && typeof sp.paymentTypes === 'object' ? sp.paymentTypes : {}
		};
	}

	enterEditMode(): void {
		const list = this.getRemittanceList();
		this.editableRemittance = list.map((rem: any) => ({
			...rem,
			lines: (rem.lines || []).map((l: any) => ({ ...l, selected: true }))
		}));
		this.resetAddState();
		this.editMode = true;
	}

	cancelEdit(): void {
		this.editMode = false;
		this.editableRemittance = [];
		this.resetAddState();
	}

	private resetAddState(): void {
		this.addPanelFor = null;
		this.loadingAddFor = null;
		this.availableByVendor = {};
		this.addError = null;
	}

	/** Toggle the "add invoices" panel for a vendor; lazy-load its open invoices. */
	toggleAddInvoices(rem: any): void {
		const sid = String(rem.SupplierID);
		if (this.addPanelFor === sid) {
			this.addPanelFor = null;
			return;
		}
		this.addPanelFor = sid;
		this.addError = null;
		if (!this.availableByVendor[sid]) {
			this.loadAvailableForVendor(rem);
		}
	}

	/**
	 * Fetch the vendor's open invoices using the same search that built the run
	 * (saved due date + currency), then drop any already present in the run.
	 */
	loadAvailableForVendor(rem: any): void {
		const sid = String(rem.SupplierID);
		const sp = this.run?.savedPayload || {};
		const hasDiscount = (rem.lines || []).some((l: any) => (l.discount || 0) > 0);
		const params = {
			supplierIds: [sid],
			// Empty saved due date → far date so every open invoice comes back.
			dueDate: sp.duedate || '2999-12-31',
			currency: sp.currency || 'USD',
			postingDate: (sp.date && sp.date[sid]) ? sp.date[sid] : (sp.duedate || ''),
			includeDiscount: hasDiscount ? '1' : '0',
			vendorType: ''
		};
		this.loadingAddFor = sid;
		this.addError = null;
		this.paymentsService.searchChecks(params).subscribe(
			(res: any) => {
				this.loadingAddFor = null;
				const rows = (res && res.checklist && res.checklist[sid]) ? res.checklist[sid] : [];
				const inRun = new Set((rem.lines || []).map((l: any) => String(l.TransID)));
				this.availableByVendor[sid] = rows.filter((r: any) => !inRun.has(String(r.id)));
			},
			() => {
				this.loadingAddFor = null;
				this.addError = 'Could not load open invoices for ' + sid + '.';
				this.availableByVendor[sid] = [];
			}
		);
	}

	availableInvoicesFor(rem: any): any[] {
		return this.availableByVendor[String(rem.SupplierID)] || [];
	}

	/** Add one open invoice to the vendor's run lines (selected, so it saves and posts). */
	addInvoiceLine(rem: any, inv: any): void {
		const sid = String(rem.SupplierID);
		const amount = Number(inv.bal != null ? inv.bal : (inv.balance != null ? inv.balance : 0));
		const discount = Number(inv.disc != null ? inv.disc : 0);
		rem.lines = rem.lines || [];
		rem.lines.push({
			TransID: inv.id,
			suppreference: inv.suppreference,
			trandate: inv.trandate,
			amount,
			discount,
			selected: true
		});
		// Ensure the rebuilt run has a post date for this vendor.
		if (this.run?.savedPayload) {
			this.run.savedPayload.date = this.run.savedPayload.date || {};
			if (!this.run.savedPayload.date[sid]) {
				this.run.savedPayload.date[sid] = rem.PostDate || this.run.savedPayload.duedate || '';
			}
		}
		this.availableByVendor[sid] = (this.availableByVendor[sid] || []).filter((r: any) => String(r.id) !== String(inv.id));
	}

	getDisplayRemittance(): any[] {
		return this.editMode ? this.editableRemittance : this.getRemittanceList();
	}

	buildPayloadFromSelection(): any {
		const sp = this.run?.savedPayload;
		if (!sp) return null;
		const include: any = {};
		const payamt: any = {};
		const discamt: any = {};
		const list = this.editMode ? this.editableRemittance : this.getRemittanceList();
		// Start from the saved map, then override with each vendor's current chip choice.
		const paymentTypes: any = { ...(sp.paymentTypes || {}) };
		for (const rem of list) {
			if (rem.SupplierID != null && rem.PaytType) {
				paymentTypes[rem.SupplierID] = rem.PaytType;
			}
			for (const line of rem.lines || []) {
				const id = line.TransID;
				const sel = this.editMode ? !!line.selected : true;
				include[id] = sel;
				payamt[id] = sel ? (line.amount || 0) : 0;
				discamt[id] = sel ? (line.discount || 0) : 0;
			}
		}
		return {
			reprint: 'true',
			journal: this.run.journal,
			include,
			payamt,
			discamt,
			date: sp.date || {},
			bankacct: sp.bankacct || '',
			currency: sp.currency || 'USD',
			duedate: sp.duedate || '',
			checkNum: sp.checkNum || '',
			vendorCheckNumbers: sp.vendorCheckNumbers || {},
			paymentTypes
		};
	}

	/** Base posting row for finalize when stored `postings` is missing or has no key for this vendor. */
	private syntheticPostingFromRemittance(rem: any): any {
		const sp = this.run?.savedPayload || {};
		return {
			PaytReference: rem.PaytReference ?? '',
			AccumBalance: rem.AccumBalance ?? 0,
			AccumDiffOnExch: 0,
			PostDate: rem.PostDate ?? '',
			ExchRate: 1,
			PaytType: rem.PaytType || 'Check',
			BankAcct: sp.bankacct || '',
			Allocs: [],
			DueDate: sp.duedate ?? '',
			SupplierID: rem.SupplierID,
			PayOrder: rem.PayOrder ?? '',
			Currency: rem.Currency || 'USD',
			AccumDisc: 0,
			journal: this.run?.journal
		};
	}

	buildPostingsFromSelection(): any {
		if (!this.run) return null;
		const raw = this.run.postings;
		const postingsSource =
			raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
		const postings: any = {};
		const list = this.editMode ? this.editableRemittance : this.getRemittanceList();
		for (const rem of list) {
			const selectedLines = (rem.lines || []).filter((l: any) => (this.editMode ? !!l.selected : true));
			if (selectedLines.length === 0) continue;
			const key = Object.keys(postingsSource).find((k: string) =>
				String(postingsSource[k].SupplierID) === String(rem.SupplierID)
			);
			const orig = key ? { ...postingsSource[key] } : this.syntheticPostingFromRemittance(rem);
			const Allocs = selectedLines.map((l: any) => ({
				TransID: l.TransID,
				Amount: (l.amount || 0) + (l.discount || 0)
			}));
			const AccumBalance = selectedLines.reduce((s: number, l: any) => s + (l.amount || 0) + (l.discount || 0), 0);
			const AccumDisc = selectedLines.reduce((s: number, l: any) => s + (l.discount || 0), 0);
			const outKey = key || String(rem.PaytReference != null && rem.PaytReference !== '' ? rem.PaytReference : rem.SupplierID);
			const PaytType = rem.PaytType || orig.PaytType || 'Check';
			postings[outKey] = { ...orig, PaytType, Allocs, AccumBalance, AccumDisc };
		}
		return Object.keys(postings).length ? postings : null;
	}

	resave(): void {
		const payload = this.buildPayloadFromSelection();
		if (!payload || !this.run?.savedPayload) return;
		this.saving = true;
		this.paymentsService.savePaymentRunForReview(payload).subscribe(
			(res: any) => {
				this.saving = false;
				if (res.success) {
					this.globalSearchService.showNotification('Run updated.', 'success', 'bottom', 'right');
					this.loadRun();
				} else {
					alert(res.error || 'Failed to save.');
				}
			},
			() => {
				this.saving = false;
				alert('Failed to save.');
			}
		);
	}

	canResave(): boolean {
		return !!(this.run?.savedPayload && this.editMode && !this.saving);
	}

	canPostFromEdit(): boolean {
		if (!this.editMode) return true;
		const list = this.editableRemittance;
		const hasAny = list.some((rem: any) => (rem.lines || []).some((l: any) => l.selected));
		return hasAny;
	}

	setVendorSelection(rem: any, selected: boolean): void {
		if (!rem.lines) return;
		rem.lines.forEach((l: any) => { l.selected = selected; });
	}

	allVendorLinesSelected(rem: any): boolean {
		return !!(rem.lines && rem.lines.length && rem.lines.every((l: any) => l.selected));
	}

	setAllLinesSelection(selected: boolean): void {
		this.editableRemittance.forEach((rem: any) => {
			if (rem.lines) rem.lines.forEach((l: any) => { l.selected = selected; });
		});
	}

	allLinesSelected(): boolean {
		const list = this.editableRemittance;
		if (!list.length) return false;
		return list.every((rem: any) => !!(rem.lines && rem.lines.length && rem.lines.every((l: any) => l.selected)));
	}

	getEditableVendorTotal(rem: any): number {
		if (!rem.lines) return 0;
		return rem.lines
			.filter((l: any) => l.selected)
			.reduce((s: number, l: any) => s + (l.amount || 0) + (l.discount || 0), 0);
	}

	openRemittancePreview(): void {
		if (!this.run) return;
		const list = this.getDisplayRemittance();
		const bank = this.run.savedPayload?.bankacct || '';
		const tabs: VendorRemitPreviewTabRequest[] = [];
		for (const rem of list) {
			const lines = (rem.lines || []).filter((l: any) => !this.editMode || l.selected);
			const allocs: Array<{ trans_id: number; amount: number }> = [];
			for (const l of lines) {
				const amt = Math.round(((l.amount || 0) + (l.discount || 0)) * 100) / 100;
				if (amt > 0 && l.TransID != null) {
					allocs.push({ trans_id: +l.TransID, amount: amt });
				}
			}
			if (!allocs.length) continue;
			const accum = lines.reduce((s: number, l: any) => s + (l.amount || 0) + (l.discount || 0), 0);
			const accumDisc = lines.reduce((s: number, l: any) => s + (l.discount || 0), 0);
			tabs.push({
				label: `${rem.SupplierID} — ${rem.PaytReference || 'ref'}`,
				supplier_id: String(rem.SupplierID),
				context: {
					payment_total: accum,
					payment_discount: accumDisc,
					payment_reference: rem.PaytReference || '',
					checkno: rem.PaytReference || '',
					payment_type: rem.PaytType || '',
					post_date: rem.PostDate || '',
					bank_account: bank,
					currency: rem.Currency || 'USD'
				},
				allocations: allocs
			});
		}
		if (!tabs.length) {
			this.globalSearchService.showNotification('No invoice lines to preview — select lines or turn off edit filters.', 'info', 'bottom', 'right');
			return;
		}
		this.dialog.open(VendorRemitPreviewModalComponent, {
			width: '760px',
			maxWidth: '98vw',
			data: {
				title: 'Remittance email preview (per vendor, same as after Approve & post)',
				tabs
			} as VendorRemitPreviewModalData
		});
	}

	private postingsToArray(postings: any): any[] {
		if (!postings) { return []; }
		if (Array.isArray(postings)) { return postings.filter((p: any) => p); }
		return Object.values(postings).filter((p: any) => p);
	}

	private async postInChunks(journal: string | number, postings: any): Promise<any> {
		const list = this.postingsToArray(postings);
		const total = list.length;
		const start = (this.postResumeIndex > 0 && this.postResumeIndex < total) ? this.postResumeIndex : 0;

		// Carry forward what a previous attempt already posted so the banner and the
		// failure message stay truthful across a resume.
		const carried = (start > 0 && this.postingProgress)
			? { posted: this.postingProgress.posted, skipped: this.postingProgress.skipped }
			: { posted: 0, skipped: 0 };
		this.postingProgress = {
			current: start,
			total,
			posted: carried.posted,
			skipped: carried.skipped,
			errors: [],
			failed: false
		};

		// Any chunk failure leaves the earlier chunks committed. Tell the operator exactly
		// where it stopped, and that re-running resumes rather than re-cutting checks.
		const failWith = (detail: string): Error => {
			this.postingProgress.failed = true;
			return new Error(
				`${detail}\n\nStopped after ${this.postingProgress.current} of ${total} payment(s) ` +
				`(${this.postingProgress.posted} posted, ${this.postingProgress.skipped} already posted). ` +
				`The run is NOT closed. Press "Approve & post" again to resume from payment ` +
				`${this.postingProgress.current + 1} — already-posted checks are skipped, not re-cut.`
			);
		};

		let lastResp: any = { success: true, done: false };
		for (let i = start; i < total; i += this.POST_CHUNK_SIZE) {
			const chunk = list.slice(i, i + this.POST_CHUNK_SIZE);
			const isLast = (i + chunk.length) >= total;

			let resp: any = null;
			try {
				resp = await this.paymentsService.postChecksChunk({
					journal,
					chunk,
					startIndex: i,
					totalCount: total,
					isLast
				}).toPromise();
			} catch (err: any) {
				const msg = err?.error?.error || err?.message || 'Network error during posting.';
				this.postingProgress.errors.push({ chunkStart: i, error: msg });
				throw failWith(msg);
			}

			if (!resp || resp.success === false) {
				const msg = resp?.error || 'Server rejected chunk.';
				this.postingProgress.errors.push({ chunkStart: i, error: msg });
				throw failWith(msg);
			}

			this.postingProgress.posted += Number(resp.posted) || 0;
			this.postingProgress.skipped += Number(resp.skipped) || 0;
			this.postingProgress.current = Math.min(i + chunk.length, total);
			if (Array.isArray(resp.errors) && resp.errors.length) {
				this.postingProgress.errors.push(...resp.errors);
				throw failWith(`Posting failed on ${resp.errors.length} item(s). ` + (resp.errors[0]?.error || ''));
			}

			// Only advance the resume point on a clean chunk.
			this.postResumeIndex = this.postingProgress.current;
			lastResp = resp;
		}

		const summaryTotal = list.reduce((sum: number, p: any) => sum + (parseFloat(p.AccumBalance) || 0), 0);
		const summaryCurrency = list[0]?.Currency || this.run?.savedPayload?.currency || 'USD';
		const uniqueVendors = new Set(list.map((p: any) => String(p.SupplierID)));
		const finalSummary = (lastResp.summary && lastResp.summary.totalAmount > 0)
			? lastResp.summary
			: {
				totalAmount: summaryTotal,
				currency: summaryCurrency,
				vendorCount: uniqueVendors.size,
				paymentCount: list.length
			};

		return {
			...lastResp,
			journal,
			summary: finalSummary
		};
	}

	async approveAndPost(): Promise<void> {
		if (this.posting) { return; }
		if (!this.run?.journal) {
			alert('Missing payment run journal. Go back to Pending payment runs and open the run again.');
			return;
		}
		let postings = this.buildPostingsFromSelection();
		let postingCount =
			postings && typeof postings === 'object' && !Array.isArray(postings)
				? Object.keys(postings).length
				: 0;
		if (!postings || postingCount === 0) {
			const fallback = this.run.postings;
			if (fallback && typeof fallback === 'object' && !Array.isArray(fallback)) {
				postings = fallback;
				postingCount = Object.keys(postings).length;
			}
		}
		if (!postings || postingCount === 0 || !hasPaymentRunPostings(postings)) {
			alert(
				'There are no payment lines to post (empty postings). If you are editing the run, select at least one invoice line per vendor, or reload this page.\n\n' +
					'Vendor remittance email is configured under Vendors → Remittance email.'
			);
			return;
		}
		this.posting = true;
		const journal = this.run.journal;
		try {
			const res = await this.postInChunks(journal, postings);
			this.posting = false;
			this.postingProgress = null;
			this.postResumeIndex = 0;
			if (res.success) {
				this.postedResult = res;
				this.run = null;
				this.loading = false;
				this.error = null;
				this.successMessage = formatPaymentRunPostedMessage(res);
				this.globalSearchService.showNotification(this.successMessage, 'success', 'bottom', 'right');
			} else {
				alert(res.error || 'Failed to post.');
				if (res.error && res.error.indexOf('already posted') !== -1) {
					this.router.navigate(['/vendors-pending-payment-runs']);
				}
			}
		} catch (err: any) {
			this.posting = false;
			const msg = err?.error?.error || err?.message || 'Failed to post.';
			// postingProgress is deliberately LEFT in place: it is the record of how far the
			// run got, and the banner keeps it on screen so the operator can see it.
			alert(msg);
			if (msg.indexOf('This run was already posted') !== -1) {
				this.postResumeIndex = 0;
				this.postingProgress = null;
				this.router.navigate(['/vendors-pending-payment-runs']);
			}
		}
	}

	printPdf(): void {
		if (!this.run?.journal || !this.paymentRunPrintArea?.nativeElement) return;
		const html = this.paymentRunPrintArea.nativeElement.innerHTML;
		if (!html) return;
		const encoded = this.globalSearchService.base64encode(html);
		const filename = `PaymentRun-${this.run.journal}.pdf`;
		this.printService.pdf({
			content: encoded,
			filename,
			landscape: true
		}).subscribe(
			(result: any) => {
				if (result?.content) {
					this.globalSearchService.downloadPdf(result.content, filename);
				} else {
					alert('Could not generate PDF.');
				}
			},
			() => alert('Failed to generate PDF.')
		);
	}

	exportXls(): void {
		if (!this.run?.journal || !this.paymentRunPrintArea?.nativeElement) return;
		const html = this.paymentRunPrintArea.nativeElement.innerHTML;
		if (!html) return;
		const encoded = this.globalSearchService.base64encode(html);
		const filename = `PaymentRun-${this.run.journal}.xlsx`;
		const today = new Date();
		this.printService.xls({
			content: encoded,
			filename,
			title: 'Payment Run',
			subtitle: `Run #${this.run.journal} – ${today.toLocaleString()}`
		}).subscribe(
			(result: any) => {
				if (result?.content) {
					this.globalSearchService.downloadXls(result.content, filename);
				} else {
					alert('Could not generate XLS.');
				}
			},
			() => alert('Failed to generate XLS.')
		);
	}

	backToList(): void {
		this.router.navigate(['/vendors-pending-payment-runs']);
	}

	toggleSummary(): void {
		this.summaryCollapsed = !this.summaryCollapsed;
	}

	getRunSummaryTotal(): number {
		const list = this.getDisplayRemittance();
		return list.reduce((sum: number, rem: any) => sum + (this.editMode ? this.getEditableVendorTotal(rem) : (rem.AccumBalance || 0)), 0);
	}

	getRunSummaryDiscount(): number {
		const list = this.getDisplayRemittance();
		let d = 0;
		list.forEach((rem: any) => {
			(rem.lines || []).forEach((l: any) => {
				if (!this.editMode || l.selected) d += l.discount || 0;
			});
		});
		return d;
	}

	getRunSummaryTransactionCount(): number {
		const list = this.getDisplayRemittance();
		return list.reduce((n: number, rem: any) => n + (this.editMode ? (rem.lines || []).filter((l: any) => l.selected).length : (rem.lines || []).length), 0);
	}

	getRunSummaryCurrency(): string {
		const list = this.getRemittanceList();
		return (list[0] && list[0].Currency) || this.run?.savedPayload?.currency || 'USD';
	}

	getRunSummaryVendorCount(): number {
		return this.getDisplayRemittance().length;
	}

	getVendorTotal(rem: any): number {
		return this.editMode ? this.getEditableVendorTotal(rem) : (rem.AccumBalance || 0);
	}

	getRemittanceList(): any[] {
		if (!this.run) return [];
		if (Array.isArray(this.run.remittance) && this.run.remittance.length > 0) return this.run.remittance;
		const postings = this.run.postings;
		if (postings && typeof postings === 'object' && !Array.isArray(postings)) {
			return Object.keys(postings).map((k: string) => {
				const p = postings[k];
				const allocs = p.Allocs || [];
				return {
					PaytReference: p.PaytReference,
					PayOrder: p.PayOrder,
					PostDate: p.PostDate,
					AccumBalance: p.AccumBalance,
					Currency: p.Currency,
					PaytType: p.PaytType,
					SupplierID: p.SupplierID,
					lines: allocs.map((a: any) => ({
						TransID: a.TransID,
						suppreference: '',
						trandate: '',
						amount: a.Amount,
						discount: 0
					}))
				};
			});
		}
		return [];
	}
}
